import { Type } from 'class-transformer';
import {
	ArrayMaxSize,
	ArrayMinSize,
	IsArray,
	IsBoolean,
	IsIn,
	IsInt,
	IsOptional,
	IsString,
	Max,
	MaxLength,
	Min,
	ValidateNested,
} from 'class-validator';
import { APOLLO_TITLE_GROUPS, type ApolloTitleGroup } from '../../constants/apollo';
import type { AddContactsToCampaignResultDto } from './contacts.dto';
import type { ApolloAccountCreditsDto } from './apollo.dto';

/**
 * PD-17612 — Apollo in daily work: "Find people" at an account, "Fill in
 * missing info" for a list or selection, and "Add new people" to a campaign.
 * Every flow shows the count and the credits first; nothing costs a credit
 * until a person presses the button that says how many.
 */

/** Most people one reveal request handles; the screens send larger choices in parts. */
export const APOLLO_REVEAL_BATCH_MAX = 25;

/** Most people "Fill in missing info" plans for at once. */
export const APOLLO_FILL_PLAN_MAX = 500;

/**
 * Why a person found in Apollo is in or out, shown on every row of
 * "Preview names first" and Find people.
 * - new_person: nobody in Workd matches; a new person (and, without a known account, a new Lead).
 * - new_at_known_account: a new person at an account Workd already has (same company name); joins it.
 * - revealed_before: paid for before and still not in Workd; added free from the reveal log.
 * - in_workd_apollo_id: a Workd person already carries this Apollo id (out).
 * - in_workd_elsewhere: a Workd person carries this Apollo id but is not on this account; Find people links them free.
 * - in_workd_at_account: the same first name and last initial already at this account (out).
 * - over_company_limit: more people at this company than "People per company" allows (out).
 */
export const APOLLO_CANDIDATE_REASONS = [
	'new_person',
	'new_at_known_account',
	'revealed_before',
	'in_workd_apollo_id',
	'in_workd_elsewhere',
	'in_workd_at_account',
	'over_company_limit',
] as const;
export type ApolloCandidateReason = (typeof APOLLO_CANDIDATE_REASONS)[number];

/** One person from a free Apollo search, screened against Workd before any credit. */
export interface ApolloCandidateDto {
	apolloPersonId: string;
	firstName: string | null;
	lastNameHidden: string | null;
	title: string | null;
	organizationName: string | null;
	hasDirectPhone: boolean;
	included: boolean;
	reason: ApolloCandidateReason;
	/** The Workd person or account the reason names, for the row ("Karen D. is already here"). */
	matchedName: string | null;
	matchedContactId: number | null;
	matchedClientId: number | null;
	/** Credits adding this person may cost: 0 when revealed before, else 1 (0 again when found in our Apollo account). */
	credits: number;
	/** Workd's pick among the results ("best fits"). */
	bestFit: boolean;
	/** The size band the person's company was counted in ("Add new people"). */
	sizeBand?: string | null;
}

/** The fields a free search result carries, sent back to reveal it (names help the free lookup first). */
export class ApolloRevealCandidateDto {
	@IsString()
	@MaxLength(64)
	public apolloPersonId!: string;

	@IsOptional()
	@IsString()
	@MaxLength(100)
	public firstName?: string | null;

	@IsOptional()
	@IsString()
	@MaxLength(100)
	public lastNameHidden?: string | null;

	@IsOptional()
	@IsString()
	@MaxLength(255)
	public title?: string | null;

	@IsOptional()
	@IsString()
	@MaxLength(255)
	public organizationName?: string | null;
}

// ---------------------------------------------------------------- Find people

export class ApolloFindPeopleRequestDto {
	@IsInt()
	@Min(1)
	public clientId!: number;

	@IsOptional()
	@IsArray()
	@ArrayMaxSize(APOLLO_TITLE_GROUPS.length)
	@IsIn(APOLLO_TITLE_GROUPS, { each: true })
	public groups?: Array<ApolloTitleGroup>;
}

export interface ApolloFindPeopleResultDto {
	clientId: number;
	clientName: string;
	/**
	 * How Apollo was asked for the account's people: its stored Apollo id, its
	 * web domains, or (with neither) its name; null when there was nothing to
	 * search by.
	 */
	searchedBy: 'apollo_org' | 'web_domain' | 'company_name' | null;
	totalEntries: number;
	people: Array<ApolloCandidateDto>;
	/**
	 * People left out because they are already on this account. People Workd
	 * has elsewhere are not counted here: they are listed, free to add.
	 */
	inWorkdCount: number;
	/** Whether the AI (Reading role) chose the best fits, or the title order did. */
	bestFitSource: 'ai' | 'titles';
	accountCredits: ApolloAccountCreditsDto | null;
	/** Credits left under the tenant's monthly Workd budget; null when there is no budget. */
	budgetLeft: number | null;
}

export class ApolloFindPeopleAddRequestDto {
	@IsInt()
	@Min(1)
	public clientId!: number;

	@IsArray()
	@ArrayMinSize(1)
	@ArrayMaxSize(APOLLO_REVEAL_BATCH_MAX)
	@ValidateNested({ each: true })
	@Type(() => ApolloRevealCandidateDto)
	public people!: Array<ApolloRevealCandidateDto>;
}

/**
 * What happened to one person a flow tried to add or fill.
 * - added: a new person (and Lead, when the company was new) was created.
 * - merged: the reveal found a person Workd already had; blanks filled, counted as a miss.
 * - filled: Fill in missing info filled at least one blank.
 * - nothing_to_fill: nothing new was found for the blanks asked for.
 * - already_in_workd: found in Workd before any credit; nothing paid.
 * - linked: already in Workd and now also on the account searched from; nothing paid.
 * - no_verified_email: Apollo's email was not verified, so the person was not added.
 * - no_match: Apollo had nobody (a reveal still costs its credit).
 * - over_cap: the credit cap was reached first; not revealed.
 * - held: Apollo's limit on reading our own contacts was reached, so the free check could not run; not revealed.
 * - refused: a Workd credit limit said no.
 * - error: Apollo or Workd failed for this person.
 */
export const APOLLO_PERSON_OUTCOMES = [
	'added',
	'merged',
	'filled',
	'nothing_to_fill',
	'already_in_workd',
	'linked',
	'no_verified_email',
	'no_match',
	'over_cap',
	'held',
	'refused',
	'error',
] as const;
export type ApolloPersonOutcome = (typeof APOLLO_PERSON_OUTCOMES)[number];

/** Where a person's details came from: Workd's own records, our Apollo account, the reveal log, or a paid reveal. */
export type ApolloDetailSource = 'workd' | 'our_apollo' | 'reveal_log' | 'reveal' | 'company_lookup';

export interface ApolloPersonOutcomeDto {
	apolloPersonId: string | null;
	name: string | null;
	outcome: ApolloPersonOutcome;
	source: ApolloDetailSource | null;
	credits: number;
	contactId: number | null;
	clientId: number | null;
	/** Whether the account was created as a new Lead for this person. */
	createdLead: boolean;
	/** The blanks filled (email, phone, title, linkedin, address, website). */
	filled: Array<string>;
	message: string | null;
}

export interface ApolloAddPeopleResultDto {
	outcomes: Array<ApolloPersonOutcomeDto>;
	creditsSpent: number;
	added: number;
	merged: number;
	/** Already in Workd and now also on the account searched from (Find people); nothing paid. */
	linked: number;
	leadsCreated: number;
	/** Not revealed because a limit or the cap came first. */
	held: number;
	/** Add new people only: what the campaign add did (the email check included). */
	campaign?: AddContactsToCampaignResultDto | null;
}

// -------------------------------------------------------- Fill in missing info

/** What "Fill in missing info" may fill. Company covers the account's address and website. */
export const APOLLO_FILL_FIELDS = ['phone', 'email', 'title_linkedin', 'company'] as const;
export type ApolloFillField = (typeof APOLLO_FILL_FIELDS)[number];

export class ApolloFillPlanRequestDto {
	@IsArray()
	@ArrayMinSize(1)
	@ArrayMaxSize(APOLLO_FILL_PLAN_MAX)
	@IsInt({ each: true })
	public contactIds!: Array<number>;

	@IsArray()
	@ArrayMinSize(1)
	@IsIn(APOLLO_FILL_FIELDS, { each: true })
	public fields!: Array<ApolloFillField>;
}

/**
 * The counts "Fill in missing info" shows before anything is spent. People
 * are counted once, at the first free source that has what they lack; the
 * rest are checked against our Apollo account (free) before any reveal, so
 * `upToRevealCredits` is a ceiling.
 */
export interface ApolloFillPlanDto {
	people: number;
	/** People missing at least one of the chosen fields. */
	needing: number;
	/** Filled from other records Workd holds (another account, a past import). */
	fromWorkd: number;
	/** Paid for before (the reveal log); free. */
	fromRevealLog: number;
	/** Looked for in our Apollo account first (free), then revealed (about one credit each). */
	toLookUp: number;
	upToRevealCredits: number;
	/** Accounts with a web domain and a blank address or website (one credit each). */
	companies: number;
	companyCredits: number;
	/** Missing a field but nothing to search Apollo by (no name, or no company). */
	cannotSearch: number;
	budgetLeft: number | null;
	accountCredits: ApolloAccountCreditsDto | null;
}

export class ApolloFillRunRequestDto {
	@IsArray()
	@ArrayMinSize(1)
	@ArrayMaxSize(APOLLO_REVEAL_BATCH_MAX)
	@IsInt({ each: true })
	public contactIds!: Array<number>;

	@IsArray()
	@ArrayMinSize(1)
	@IsIn(APOLLO_FILL_FIELDS, { each: true })
	public fields!: Array<ApolloFillField>;

	/** Credits this part may spend at most (the screen sends what is left of the person's cap). */
	@IsInt()
	@Min(0)
	@Max(100_000)
	public creditCap!: number;
}

// ------------------------------------------------- Add new people to a campaign

export class ApolloAudienceRequestDto {
	@IsInt()
	@Min(1)
	public campaignId!: number;

	/** Apollo location text: a city, metro, state or ZIP ("Anaheim, California"). */
	@IsArray()
	@ArrayMinSize(1)
	@ArrayMaxSize(10)
	@IsString({ each: true })
	@MaxLength(100, { each: true })
	public locations!: Array<string>;

	/** The kind of company, as Apollo company keywords ("distributor", "fasteners"). */
	@IsArray()
	@ArrayMaxSize(10)
	@IsString({ each: true })
	@MaxLength(60, { each: true })
	public companyKeywords!: Array<string>;

	@IsArray()
	@ArrayMinSize(1)
	@ArrayMaxSize(APOLLO_TITLE_GROUPS.length)
	@IsIn(APOLLO_TITLE_GROUPS, { each: true })
	public groups!: Array<ApolloTitleGroup>;

	/** Size band keys (APOLLO_SIZE_BANDS) to count. */
	@IsArray()
	@ArrayMinSize(1)
	@ArrayMaxSize(10)
	@IsString({ each: true })
	@MaxLength(20, { each: true })
	public sizeBands!: Array<string>;

	@IsInt()
	@Min(1)
	@Max(5)
	public peoplePerCompany!: number;

	/** Include each person's row with the reason they're in or out ("Preview names first"). */
	@IsOptional()
	@IsBoolean()
	public includePeople?: boolean;
}

export interface ApolloSizeBandCountDto {
	key: string;
	label: string;
	/** People Apollo found in this band (free search), after its own Synced and Blocklist exclusion. */
	found: number;
	/** People looked at (Workd reads at most a few pages per band; `found` may be larger). */
	screened: number;
	inWorkd: number;
	newCompanies: number;
	knownCompanies: number;
	newPeople: number;
	credits: number;
}

export interface ApolloAudienceCountDto {
	campaignId: number;
	campaignName: string;
	/** Apollo's total for the search before its own exclusions. */
	foundBeforeExclusions: number;
	/** People Apollo left out because they're labeled Synced or Blocklist in our Apollo account. */
	leftOutByApollo: number;
	bands: Array<ApolloSizeBandCountDto>;
	/** Every screened person with the reason they're in or out, when asked for. */
	people: Array<ApolloCandidateDto>;
	/** Revealed people last month who were in Workd anyway (the miss rate). */
	lastMonthMisses: number;
	lastMonthReveals: number;
	budgetLeft: number | null;
	accountCredits: ApolloAccountCreditsDto | null;
}

export class ApolloAudienceAddRequestDto {
	@IsInt()
	@Min(1)
	public campaignId!: number;

	@IsArray()
	@ArrayMinSize(1)
	@ArrayMaxSize(APOLLO_REVEAL_BATCH_MAX)
	@ValidateNested({ each: true })
	@Type(() => ApolloRevealCandidateDto)
	public people!: Array<ApolloRevealCandidateDto>;

	/** Credits this part may spend at most (what is left of the person's cap). */
	@IsInt()
	@Min(0)
	@Max(100_000)
	public creditCap!: number;
}
